/**
 * Session Guard — Protege contra perda de dados ao logar em múltiplos dispositivos.
 *
 * Fluxo:
 * 1. Ao fazer login, verifica se há sessões ativas em outros dispositivos
 * 2. Se houver, salva snapshot dos dados atuais antes de prosseguir
 * 3. Registra a sessão atual com heartbeat periódico
 * 4. Remove a sessão ao fazer logout ou fechar a aba
 */
import { supabase } from '../lib/supabase';

// Token único por aba/dispositivo (persiste na aba, morre ao fechar)
const SESSION_TOKEN_KEY = 'kzerodiary_session_token';

function getOrCreateSessionToken() {
  let token = sessionStorage.getItem(SESSION_TOKEN_KEY);
  if (!token) {
    token = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  }
  return token;
}

function getDeviceInfo() {
  const ua = navigator.userAgent;
  // Resumo legível: "Windows Chrome", "iPhone Safari", etc.
  const os = /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'Mac' : /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : 'Outro';
  const browser = /Edg/.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome' : /Safari/.test(ua) ? 'Safari' : /Firefox/.test(ua) ? 'Firefox' : 'Outro';
  return `${os} — ${browser}`;
}

/**
 * Verifica se há sessões ativas em outros dispositivos.
 * Retorna array de sessões (vazio = sem conflito).
 */
export async function verificarSessoesAtivas() {
  const token = getOrCreateSessionToken();
  try {
    const { data, error } = await supabase.rpc('verificar_sessoes_ativas', {
      p_session_token: token,
    });
    if (error) {
      console.warn('Erro ao verificar sessões:', error.message);
      return [];
    }
    return data || [];
  } catch {
    return [];
  }
}

/**
 * Salva snapshot dos dados atuais no histórico (antes de permitir novo login).
 */
export async function salvarSnapshotConflito() {
  const device = getDeviceInfo();
  try {
    const { data, error } = await supabase.rpc('salvar_snapshot_historico', {
      p_motivo: 'login_conflito',
      p_device_info: device,
    });
    if (error) console.warn('Erro ao salvar snapshot:', error.message);
    return data; // UUID do snapshot
  } catch {
    return null;
  }
}

/**
 * Registra/atualiza heartbeat da sessão atual.
 */
export async function registrarSessao(userEmail) {
  const token = getOrCreateSessionToken();
  const device = getDeviceInfo();
  try {
    await supabase.rpc('registrar_sessao', {
      p_session_token: token,
      p_device_info: device,
      p_user_email: userEmail || null,
    });
  } catch {}
}

/**
 * Remove a sessão ao logout/fechar aba.
 */
export async function removerSessao() {
  const token = sessionStorage.getItem(SESSION_TOKEN_KEY);
  if (!token) return;
  try {
    await supabase.rpc('remover_sessao', { p_session_token: token });
  } catch {}
}

let heartbeatInterval = null;

/**
 * Inicia heartbeat (60s). Chame no login.
 */
export function iniciarHeartbeat(userEmail) {
  pararHeartbeat();
  registrarSessao(userEmail);
  heartbeatInterval = setInterval(() => registrarSessao(userEmail), 60_000);

  // Cleanup ao fechar aba
  window.addEventListener('beforeunload', removerSessao);
}

/**
 * Para heartbeat. Chame no logout.
 */
export function pararHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
  window.removeEventListener('beforeunload', removerSessao);
}

export { getDeviceInfo, getOrCreateSessionToken };
